import { type FormEvent, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';

const MAX_REASON_LENGTH = 500;

/**
 * Asks for the (optional) reason before an entry is rejected. The author sees the reason
 * on their entry, so it should say what they could do better.
 */
export function RejectSubmissionModal({
  title,
  initialReason = '',
  isLoading,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  /** Suggested reason, e.g. what is wrong with the image. */
  initialReason?: string;
  isLoading: boolean;
  error?: string;
  onConfirm: (reason: string | undefined) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState(initialReason);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onConfirm(reason.trim() || undefined);
  }

  return (
    <Modal title="Zavrni prispevek" onClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <p className="text-sm text-fg-secondary">
          Prispevek »{title}« bo zavrnjen. Avtor bo razlog videl ob svojem prispevku.
        </p>
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex flex-col gap-1">
          <label htmlFor="reject-reason" className="text-sm font-medium text-fg">
            Razlog (neobvezno)
          </label>
          <textarea
            id="reject-reason"
            value={reason}
            maxLength={MAX_REASON_LENGTH}
            rows={4}
            autoFocus
            placeholder="Na primer: slika je premajhna ali ne ustreza temi."
            onChange={(e) => setReason(e.target.value)}
            className="rounded-control border border-line-strong bg-surface-0 px-3 py-2 text-sm text-fg transition-colors duration-fast placeholder:text-fg-muted"
          />
          <span className="text-right text-xs text-fg-muted">
            {reason.length}/{MAX_REASON_LENGTH}
          </span>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Prekliči
          </Button>
          <Button type="submit" variant="danger" isLoading={isLoading}>
            Zavrni
          </Button>
        </div>
      </form>
    </Modal>
  );
}
